import React, { useEffect, useState } from 'react';
import { getPlansAdmin, updatePlan, getAddonsAdmin, updateAddon, getPurchasesPaused, updatePurchasesPaused, getTrialSettings, updateTrialSettings } from '../../services/planService';

const AdminPricing = () => {
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [successKey, setSuccessKey] = useState(null);
  const [purchasesPaused, setPurchasesPausedState] = useState(false);
  const [pauseSaving, setPauseSaving] = useState(false);

  const [trialEnabled, setTrialEnabled] = useState(true);
  const [trialDays, setTrialDays] = useState(30);
  const [trialSaving, setTrialSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [plansData, addonsData, pausedData, trialData] = await Promise.all([
        getPlansAdmin(), getAddonsAdmin(), getPurchasesPaused(), getTrialSettings(),
      ]);
      setPlans(plansData.plans);
      setAddons(addonsData.addons);
      setPurchasesPausedState(pausedData.paused);
      setTrialEnabled(trialData.enabled);
      setTrialDays(trialData.days);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePaused = async () => {
    setPauseSaving(true);
    try {
      const data = await updatePurchasesPaused(!purchasesPaused);
      setPurchasesPausedState(data.paused);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setPauseSaving(false);
    }
  };

  const handleSaveTrialSettings = async (nextEnabled, nextDays) => {
    setTrialSaving(true);
    try {
      const data = await updateTrialSettings(nextEnabled, nextDays);
      setTrialEnabled(data.enabled);
      setTrialDays(data.days);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update trial settings');
    } finally {
      setTrialSaving(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handlePlanFieldChange = (planKey, field, value) => {
    setPlans((prev) => prev.map((p) => (p.plan_key === planKey ? { ...p, [field]: value } : p)));
  };

  const handleAddonFieldChange = (addonKey, field, value) => {
    setAddons((prev) => prev.map((a) => (a.addon_key === addonKey ? { ...a, [field]: value } : a)));
  };

  const handleSavePlan = async (plan) => {
    setSavingKey(plan.plan_key);
    setError('');
    setSuccessKey(null);
    try {
      await updatePlan(plan.plan_key, {
        name: plan.name,
        price_cents: Math.round(Number(plan.price_dollars_input ?? plan.price_cents / 100) * 100),
        max_members: Number(plan.max_members),
        included_scans: Number(plan.included_scans),
      });
      setSuccessKey(plan.plan_key);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to save ${plan.name}`);
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveAddon = async (addon) => {
    setSavingKey(addon.addon_key);
    setError('');
    setSuccessKey(null);
    try {
      const payload = {
        name: addon.name,
        price_cents: Math.round(Number(addon.price_dollars_input ?? addon.price_cents / 100) * 100),
      };
      if (addon.addon_key === 'scan_pack') {
        payload.scan_amount = Number(addon.scan_amount);
      }
      await updateAddon(addon.addon_key, payload);
      setSuccessKey(addon.addon_key);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to save ${addon.name}`);
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <p style={styles.status}>Loading pricing...</p>;

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>💰 Manage Pricing</h2>
      <p style={styles.subtitle}>
        Changing a price creates a new Stripe Price automatically — existing customers keep whatever they already paid for.
      </p>

      {error && <p style={styles.error}>{error}</p>}

      <div style={purchasesPaused ? styles.pauseBannerActive : styles.pauseBanner}>
        <div>
          <p style={styles.pauseTitle}>
            {purchasesPaused ? '⏸️ New signups are currently PAUSED' : '▶️ New signups are currently open'}
          </p>
          <p style={styles.pauseSubtitle}>
            {purchasesPaused
              ? 'The pricing page shows a "temporarily unavailable" message and new purchases are blocked. Existing groups (upgrades, scan packs) are unaffected.'
              : 'Anyone can create a group and purchase a plan right now.'}
          </p>
        </div>
        <button onClick={handleTogglePaused} style={purchasesPaused ? styles.resumeBtn : styles.pauseBtn} disabled={pauseSaving}>
          {pauseSaving ? 'Saving...' : purchasesPaused ? 'Resume Signups' : 'Pause New Signups'}
        </button>
      </div>

      <div style={styles.trialBox}>
        <div>
          <p style={styles.pauseTitle}>
            🎁 Free Trial for New Signups: {trialEnabled ? `${trialDays} days` : 'Off'}
          </p>
          <p style={styles.pauseSubtitle}>
            When on, every brand-new cookbook gets full access (recipes, invites, scans, meal
            planner, PDF export) for this many days before needing to pay. When it ends, viewing
            stays open but adding, editing, inviting, meal planning and PDF export pause until they buy a plan.
          </p>
        </div>
        <div style={styles.trialControls}>
          <label style={styles.trialToggleLabel}>
            <input
              type="checkbox"
              checked={trialEnabled}
              onChange={(e) => handleSaveTrialSettings(e.target.checked, trialDays)}
              disabled={trialSaving}
            />
            Enabled
          </label>
          <input
            type="number"
            min="1"
            max="365"
            value={trialDays}
            onChange={(e) => setTrialDays(Number(e.target.value))}
            onBlur={() => handleSaveTrialSettings(trialEnabled, trialDays)}
            disabled={trialSaving || !trialEnabled}
            style={styles.trialDaysInput}
          />
          <span style={styles.pauseSubtitle}>days</span>
        </div>
      </div>

      <h3 style={styles.sectionTitle}>Base Plans</h3>
      <div style={styles.table}>
        {plans.map((plan) => (
          <div key={plan.plan_key} style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Plan</label>
              <input
                type="text"
                value={plan.name}
                onChange={(e) => handlePlanFieldChange(plan.plan_key, 'name', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Price (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                defaultValue={(plan.price_cents / 100).toFixed(2)}
                onChange={(e) => handlePlanFieldChange(plan.plan_key, 'price_dollars_input', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Max Members</label>
              <input
                type="number"
                min="1"
                value={plan.max_members}
                onChange={(e) => handlePlanFieldChange(plan.plan_key, 'max_members', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Included Scans</label>
              <input
                type="number"
                min="0"
                value={plan.included_scans}
                onChange={(e) => handlePlanFieldChange(plan.plan_key, 'included_scans', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Stripe Status</label>
              <span style={plan.stripe_price_id ? styles.synced : styles.notSynced}>
                {plan.stripe_price_id ? '✓ Synced' : 'Not yet synced'}
              </span>
            </div>
            <button onClick={() => handleSavePlan(plan)} style={styles.saveBtn} disabled={savingKey === plan.plan_key}>
              {savingKey === plan.plan_key ? 'Saving...' : 'Save'}
            </button>
            {successKey === plan.plan_key && <span style={styles.savedTag}>Saved ✓</span>}
          </div>
        ))}
      </div>

      <h3 style={{ ...styles.sectionTitle, marginTop: '32px' }}>Add-ons</h3>
      <p style={styles.subtitle}>
        Purchasable by groups that already have an active plan — the Starter → Community upgrade, and scan packs.
      </p>
      <div style={styles.table}>
        {addons.map((addon) => (
          <div key={addon.addon_key} style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Add-on</label>
              <input
                type="text"
                value={addon.name}
                onChange={(e) => handleAddonFieldChange(addon.addon_key, 'name', e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Price (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                defaultValue={(addon.price_cents / 100).toFixed(2)}
                onChange={(e) => handleAddonFieldChange(addon.addon_key, 'price_dollars_input', e.target.value)}
                style={styles.input}
              />
            </div>
            {addon.addon_key === 'scan_pack' && (
              <div style={styles.field}>
                <label style={styles.label}>Scans Granted</label>
                <input
                  type="number"
                  min="1"
                  value={addon.scan_amount}
                  onChange={(e) => handleAddonFieldChange(addon.addon_key, 'scan_amount', e.target.value)}
                  style={styles.input}
                />
              </div>
            )}
            <div style={styles.field}>
              <label style={styles.label}>Stripe Status</label>
              <span style={addon.stripe_price_id ? styles.synced : styles.notSynced}>
                {addon.stripe_price_id ? '✓ Synced' : 'Not yet synced'}
              </span>
            </div>
            <button onClick={() => handleSaveAddon(addon)} style={styles.saveBtn} disabled={savingKey === addon.addon_key}>
              {savingKey === addon.addon_key ? 'Saving...' : 'Save'}
            </button>
            {successKey === addon.addon_key && <span style={styles.savedTag}>Saved ✓</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  title: { color: '#e67e22', marginBottom: '6px' },
  subtitle: { color: '#888', fontSize: '13px', marginBottom: '24px' },
  sectionTitle: { color: '#333', fontSize: '16px', marginBottom: '12px' },
  pauseBanner: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
    padding: '16px 20px', borderRadius: '10px', marginBottom: '28px',
    backgroundColor: '#eafaf1', border: '1px solid #b8e6ca',
  },
  pauseBannerActive: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
    padding: '16px 20px', borderRadius: '10px', marginBottom: '28px',
    backgroundColor: '#fdf0e3', border: '1px solid #f0c896',
  },
  pauseTitle: { margin: '0 0 4px 0', fontWeight: '700', color: '#333', fontSize: '14px' },
  pauseSubtitle: { margin: 0, color: '#777', fontSize: '12px', maxWidth: '480px' },
  pauseBtn: {
    padding: '10px 18px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '13px',
  },
  resumeBtn: {
    padding: '10px 18px', backgroundColor: '#27ae60', color: 'white', border: 'none',
    borderRadius: '8px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '13px',
  },
  trialBox: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
    padding: '16px 20px', borderRadius: '10px', marginBottom: '28px',
    backgroundColor: '#f5f0fa', border: '1px solid #ddc9ee',
  },
  trialControls: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  trialToggleLabel: {
    display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555',
    fontWeight: '600', whiteSpace: 'nowrap',
  },
  trialDaysInput: {
    width: '60px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px',
  },
  table: { display: 'flex', flexDirection: 'column', gap: '16px' },
  row: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '16px',
    padding: '16px',
    border: '1px solid #eee',
    borderRadius: '10px',
    flexWrap: 'wrap',
  },
  field: { display: 'flex', flexDirection: 'column', minWidth: '110px' },
  label: { fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: '600' },
  input: { padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' },
  synced: { color: '#27ae60', fontSize: '13px', fontWeight: '600' },
  notSynced: { color: '#e67e22', fontSize: '13px', fontWeight: '600' },
  saveBtn: {
    padding: '10px 20px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  savedTag: { color: '#27ae60', fontSize: '13px', fontWeight: '600' },
  status: { textAlign: 'center', color: '#999' },
  error: { color: '#e74c3c', marginBottom: '16px' },
};

export default AdminPricing;
